import React from "react";
import { IconButton } from "@mui/joy";
import { Tooltip } from "@mui/material";
import { ViewColumnRounded } from "@mui-symbols-material/w300";
import { GridPreferencePanelsValue } from "@mui/x-data-grid-pro";
import { GridApiPro } from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";

interface Props {
    apiRef: React.MutableRefObject<GridApiPro>;
    // Forwarded to the grid as panel anchor so the columns panel opens
    // underneath this button instead of at the grid's top left corner
    buttonRef?: React.Ref<HTMLButtonElement>;
}

const ManageColumnsButton = ({ apiRef, buttonRef }: Props) => {
    const { t } = useTranslation();

    // Panel state is read imperatively inside the handlers: this button lives
    // outside the grid and can render before the grid state exists.
    const isColumnsPanelOpen = () => {
        const panelState = apiRef.current?.state?.preferencePanel;
        return !!panelState?.open && panelState.openedPanelValue === GridPreferencePanelsValue.columns;
    };

    return (
        <Tooltip title={t("Manage columns")}>
            <IconButton
                ref={buttonRef}
                data-testid="manage-columns-button"
                variant="outlined"
                color="neutral"
                sx={{ ml: "5px", flexShrink: 0 }}
                onPointerUp={(event) => {
                    // The panel closes itself via a ClickAwayListener on pointerup.
                    // Stop the event here so a click on this button reaches onClick
                    // with the panel still open and can toggle it closed.
                    if (apiRef.current?.state?.preferencePanel?.open) {
                        event.stopPropagation();
                    }
                }}
                onClick={() => {
                    if (!apiRef.current?.showPreferences) return;

                    if (isColumnsPanelOpen()) {
                        apiRef.current.hidePreferences();
                    } else {
                        apiRef.current.showPreferences(GridPreferencePanelsValue.columns);
                    }
                }}
            >
                <ViewColumnRounded />
            </IconButton>
        </Tooltip>
    );
};

export default ManageColumnsButton;
